import { useState } from 'react';
import { X, AlertTriangle, Bug, Thermometer, MapPin, ExternalLink, CheckCircle, Sparkles, Loader2, Droplets, Leaf, Activity, Info, ClipboardList, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified Alert Detail Modal
 * Opens when clicking any alert (geo-alert or engine alert)
 * Enhanced to support remote sensing alerts with comprehensive guidance
 */
const AlertDetailModal = ({ alert, onClose, onDismiss, dismissing }) => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [aiAdvice, setAiAdvice] = useState(alert?.ai_advice || null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);
  const [showFullExplanation, setShowFullExplanation] = useState(false);
  
  if (!alert) return null;

  const isGeoAlert = alert.agronomist_name || alert.distance_km !== undefined;
  const isEngineAlert = alert.type && (alert.why || alert.actions);
  const isRemoteSensingAlert = alert.type === 'remote_sensing' || alert.subtype;
  const hasEnhancedGuidance = alert.what_it_means || alert.scout_checklist;

  const getSeverityStyle = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'critical':
      case 'act_48h':
        return { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'rgba(239,68,68,0.3)' };
      case 'medium':
      case 'warning':
      case 'check_5d':
        return { background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' };
      default:
        return { background: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: 'rgba(59,130,246,0.3)' };
    }
  };

  const getAlertIcon = () => {
    // Remote sensing specific icons
    if (alert.subtype === 'moisture_stress') {
      return <Droplets size={22} />;
    }
    if (alert.subtype === 'chlorophyll_stress') {
      return <Activity size={22} />;
    }
    if (alert.subtype === 'vegetation_decline' || alert.subtype === 'early_stand') {
      return <Leaf size={22} />;
    }
    if (alert.type?.includes('disease') || alert.type?.includes('pest') || alert.alert_type?.includes('pest')) {
      return <Bug size={22} />;
    }
    if (alert.type?.includes('weather') || alert.alert_type?.includes('stress')) {
      return <Thermometer size={22} />;
    }
    return <AlertTriangle size={22} />;
  };

  // Get subtype display color
  const getSubtypeColor = () => {
    switch (alert.subtype) {
      case 'moisture_stress': return '#3b82f6';
      case 'chlorophyll_stress': return '#8b5cf6';
      case 'vegetation_decline': return '#22c55e';
      case 'early_stand': return '#f59e0b';
      default: return null;
    }
  };

  const handleOpenField = () => {
    if (alert.fieldId || alert.field_id) {
      navigate(`/fields/${alert.fieldId || alert.field_id}`);
      onClose();
    }
  };

  const handleGenerateAdvice = async () => {
    const fieldId = alert.fieldId || alert.field_id;
    // Support multiple fingerprint field names
    const fingerprint = alert.alert_fingerprint || alert.alert_id || alert.dedupe_key || alert.id;
    
    if (!fieldId || !fingerprint) {
      toast.error('Cannot generate advice for this alert');
      return;
    }
    
    setGeneratingAdvice(true);
    try {
      const response = await axios.post(
        `${API}/fields/${fieldId}/alerts/${fingerprint}/ai-advice`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.ai_advice) {
        setAiAdvice(response.data.ai_advice);
        toast.success(response.data.from_cache ? 'AI advice loaded' : 'AI advice generated');
      } else {
        toast.error(response.data.message || 'Could not generate advice');
      }
    } catch (error) {
      console.error('Error generating AI advice:', error);
      toast.error('Failed to generate AI advice');
    } finally {
      setGeneratingAdvice(false);
    }
  };

  const severityStyle = getSeverityStyle(alert.severity || alert.level);

  return (
    <div 
      className="modal-overlay"
      onClick={onClose}
      data-testid="alert-detail-modal-backdrop"
    >
      <div 
        className="card-floating anim-scale"
        onClick={(e) => e.stopPropagation()}
        data-testid="alert-detail-modal"
        style={{ maxWidth: 520, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{
          padding: 16,
          borderBottom: '1px solid var(--border-soft)',
          background: severityStyle.background,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ color: severityStyle.color }}>{getAlertIcon()}</div>
            <div>
              <h2 style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>
                {alert.title || alert.subtype || alert.alert_type?.replace(/_/g, ' ') || 'Alert Details'}
              </h2>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {alert.type?.replace(/_/g, ' ') || alert.alert_type?.replace(/_/g, ' ') || 'Alert'}
                {alert.severity && ` • ${alert.severity.replace(/_/g, ' ')}`}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
            data-testid="close-alert-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 16, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Main message */}
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
            {alert.summary || alert.message || alert.description || 'No description available'}
          </p>

          {/* Enhanced Remote Sensing Alert: What This Means */}
          {hasEnhancedGuidance && alert.what_it_means && (
            <div style={{
              padding: 14,
              background: 'rgba(59,130,246,0.08)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 12
            }}>
              <div 
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}
                onClick={() => setShowFullExplanation(!showFullExplanation)}
              >
                <Info size={16} style={{ color: '#3b82f6' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#3b82f6' }}>What This Means</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  {showFullExplanation ? 'show less' : 'show more'}
                </span>
              </div>
              <p style={{ 
                fontSize: 13, 
                color: 'var(--text-secondary)', 
                lineHeight: 1.6,
                maxHeight: showFullExplanation ? 'none' : '3.2em',
                overflow: 'hidden'
              }}>
                {alert.what_it_means}
              </p>
            </div>
          )}

          {/* Enhanced: Metrics Table */}
          {alert.metrics && (
            <div style={{
              padding: 14,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border-soft)',
              borderRadius: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <BarChart3 size={16} style={{ color: getSubtypeColor() || 'var(--text-secondary)' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)' }}>
                  {alert.metrics.index || 'Index'} Metrics
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Current</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                    {alert.metrics.current?.toFixed(3) || 'N/A'}
                  </div>
                </div>
                {alert.metrics.previous !== null && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Previous</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {alert.metrics.previous?.toFixed(3) || 'N/A'}
                    </div>
                  </div>
                )}
                {alert.metrics.baseline !== null && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Baseline</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {alert.metrics.baseline?.toFixed(3) || 'N/A'}
                    </div>
                  </div>
                )}
              </div>
              {(alert.metrics.delta !== undefined || alert.metrics.pct_delta !== undefined) && (
                <div style={{ 
                  marginTop: 12, 
                  paddingTop: 12, 
                  borderTop: '1px solid var(--border-soft)',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 24
                }}>
                  {alert.metrics.delta !== undefined && (
                    <div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Change: </span>
                      <span style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        color: alert.metrics.delta < 0 ? 'var(--danger)' : 'var(--success)' 
                      }}>
                        {alert.metrics.delta > 0 ? '+' : ''}{alert.metrics.delta?.toFixed(3)}
                      </span>
                    </div>
                  )}
                  {alert.metrics.pct_delta !== undefined && (
                    <div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Percent: </span>
                      <span style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        color: alert.metrics.pct_delta < 0 ? 'var(--danger)' : 'var(--success)' 
                      }}>
                        {alert.metrics.pct_delta > 0 ? '+' : ''}{alert.metrics.pct_delta?.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Enhanced: Data Quality */}
          {alert.data_quality && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12, 
              fontSize: 12, 
              color: 'var(--text-muted)',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: 8
            }}>
              <span>Data Quality:</span>
              <span style={{ 
                padding: '2px 8px', 
                borderRadius: 4, 
                fontSize: 11,
                fontWeight: 600,
                background: alert.data_quality.quality_flag === 'good' ? 'rgba(34,197,94,0.2)' :
                           alert.data_quality.quality_flag === 'fair' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
                color: alert.data_quality.quality_flag === 'good' ? '#22c55e' :
                       alert.data_quality.quality_flag === 'fair' ? '#f59e0b' : '#ef4444'
              }}>
                {alert.data_quality.quality_flag?.toUpperCase()}
              </span>
              <span>Cloud: {alert.data_quality.cloud_pct?.toFixed(0)}%</span>
              {alert.confidence && (
                <>
                  <span>•</span>
                  <span>Confidence: {alert.confidence}</span>
                </>
              )}
            </div>
          )}

          {/* AI Advice Section */}
          {(isEngineAlert || aiAdvice) && !hasEnhancedGuidance && (
            <div style={{
              padding: 14,
              background: aiAdvice ? 'rgba(198,170,88,0.08)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${aiAdvice ? 'rgba(198,170,88,0.25)' : 'var(--border-soft)'}`,
              borderRadius: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: aiAdvice ? 10 : 0 }}>
                <Sparkles size={16} style={{ color: 'var(--gold)' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gold)' }}>AI Advice</span>
                {alert.ai_advice && !aiAdvice && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>cached</span>
                )}
              </div>
              
              {aiAdvice ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {aiAdvice}
                </p>
              ) : (
                <button
                  onClick={handleGenerateAdvice}
                  disabled={generatingAdvice}
                  className="btn-depth-secondary"
                  style={{ width: '100%', padding: '10px 14px', fontSize: 13, marginTop: 8 }}
                  data-testid="generate-ai-advice-btn"
                >
                  {generatingAdvice ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Generate AI Advice
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Geo-alert specific info */}
          {isGeoAlert && (
            <>
              {alert.distance_km !== undefined && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                  <MapPin size={16} />
                  <span>{alert.distance_km} km from your farm</span>
                </div>
              )}
              {alert.agronomist_name && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  <span style={{ fontWeight: 600 }}>Reported by:</span> {alert.agronomist_name}
                </div>
              )}
              {alert.photo_url && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Photo Evidence:</p>
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${alert.photo_url}`}
                    alt="Alert evidence"
                    style={{ width: '100%', borderRadius: 10 }}
                  />
                </div>
              )}
            </>
          )}

          {/* Engine alert specific info */}
          {isEngineAlert && (
            <>
              {alert.why && alert.why.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Why this alert:</p>
                  <ul style={{ fontSize: 13, color: 'var(--text-muted)', listStyle: 'none', padding: 0, margin: 0 }}>
                    {alert.why.map((reason, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <span style={{ color: 'var(--warning)', marginTop: 2 }}>•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {alert.actions && alert.actions.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Recommended Actions:</p>
                  <ul style={{ fontSize: 13, color: 'var(--text-muted)', listStyle: 'none', padding: 0, margin: 0 }}>
                    {alert.actions.map((action, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <span style={{ color: 'var(--success)', marginTop: 2 }}>→</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {alert.risk_score !== undefined && (
                <div style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Risk Score:</span>{' '}
                  <span style={{ 
                    fontWeight: 700,
                    color: alert.risk_score >= 70 ? 'var(--danger)' :
                           alert.risk_score >= 40 ? 'var(--warning)' : 'var(--success)'
                  }}>
                    {alert.risk_score}/100
                  </span>
                </div>
              )}
            </>
          )}

          {/* Enhanced: Scout Checklist (for remote sensing alerts) */}
          {alert.scout_checklist && alert.scout_checklist.length > 0 && (
            <div style={{
              padding: 14,
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.2)',
              borderRadius: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <ClipboardList size={16} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#f59e0b' }}>Scout Checklist</span>
              </div>
              <ul style={{ fontSize: 13, color: 'var(--text-secondary)', listStyle: 'none', padding: 0, margin: 0 }}>
                {alert.scout_checklist.map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                    <span style={{ color: '#f59e0b', marginTop: 2 }}>☐</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations (common) */}
          {alert.recommendations && (
            <div style={{
              padding: 12,
              background: 'rgba(59,130,246,0.1)',
              borderRadius: 10,
              border: '1px solid rgba(59,130,246,0.2)'
            }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6', marginBottom: 4 }}>Recommendations:</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{alert.recommendations}</p>
            </div>
          )}

          {/* Field name if available */}
          {(alert.fieldName || alert.field_name) && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 600 }}>Field:</span> {alert.fieldName || alert.field_name}
            </div>
          )}

          {/* Date */}
          {(alert.created_at || alert.created_at_utc) && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Created: {new Date(alert.created_at || alert.created_at_utc).toLocaleString()}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          padding: 14,
          borderTop: '1px solid var(--border-soft)',
          display: 'flex',
          gap: 10
        }}>
          {(alert.fieldId || alert.field_id) && (
            <button
              onClick={handleOpenField}
              className="btn-depth-primary"
              style={{ flex: 1, padding: '10px 14px', fontSize: 13 }}
              data-testid="open-field-btn"
            >
              <ExternalLink size={16} />
              Open Field
            </button>
          )}
          
          {onDismiss && (
            <button
              onClick={() => onDismiss(alert)}
              disabled={dismissing}
              className="btn-depth-secondary"
              style={{ flex: 1, padding: '10px 14px', fontSize: 13 }}
              data-testid="dismiss-alert-btn"
            >
              {dismissing ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;
